"use client"

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Plus, Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput, WidgetDirection, WidgetOrientation } from "../GlobalWidgetEditor";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";
import FileUploadInput from "../../input/MultipleFileUploadInput";

export interface ThirdgridItem {
    type: 'text' | 'image';
    width?: string;
    direction?: WidgetDirection;
    orientation?: WidgetOrientation;
    title?: string;
    title_en?: string;
    title_ar?: string;
    description?: string;
    description_en?: string;
    description_ar?: string;
    buttonContent?: string;
    buttonContent_en?: string;
    buttonContent_ar?: string;
    link?: string;
    image?: any;
}

interface ThirdgridWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const ThirdgridWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: ThirdgridWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            type: 'text',
            width: '1/1',
            direction: 'left',
            orientation: 'horizontal',
            title: '',
            buttonContent: '',
            link: '',
            image: undefined,
        });
    };

    const removeItem = (thirdgridIndex: number) => {
        if (fields.length <= 1) return;
        remove(thirdgridIndex);
    };

    return (
        <>
            {fields.map((field, thirdgridIndex) => {
                const imageError = getError(
                    `${name}.${widgetIndex}.items.${thirdgridIndex}.image`
                ) as string | undefined;

                const itemType = (field as any).type as 'text' | 'image';

                return (
                    <div key={field.id} className="border p-4 rounded-md space-y-4">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                {dictionary.forms.item} {thirdgridIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(thirdgridIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${thirdgridIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${thirdgridIndex}.buttonContent`}
                            label={dictionary.forms?.buttonContent}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">
                                {dictionary.forms?.link}
                            </Label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${thirdgridIndex}.link`}
                                render={({ field: linkField }) => (
                                    <LinkSelector
                                        value={linkField.value || ''}
                                        onChange={linkField.onChange}
                                        placeholder={dictionary.forms?.link}
                                    />
                                )}
                            />
                        </div>
                        <Controller
                            control={control}
                            name={`${name}.${widgetIndex}.items.${thirdgridIndex}.image`}
                            render={({ field: imageField }) => (
                                <FileUploadInput
                                    accept="image/*"
                                    disabled={disabled}
                                    onUploadSuccess={(image) => {
                                        if (!Array.isArray(image) && image) imageField.onChange(image);
                                    }}
                                    defaultImages={
                                        imageField.value
                                            ? [
                                                {
                                                    ...imageField.value,
                                                    name: imageField.value.name || '',
                                                    description: imageField.value.description || '',
                                                },
                                            ]
                                            : []
                                    }
                                    onDefaultRemove={() => imageField.onChange(undefined)}
                                    error={imageError}
                                />
                            )}
                        />

                    </div>
                );
            })}

            <Button
                type="button"
                variant="outline"
                onClick={addItem}
                disabled={disabled}
                className="mt-2"
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};
